import classNames from "classnames";
import * as React from "react";
import { FC, useCallback, useMemo, useRef } from "react";
import { Switcher, SwitcherWithHint } from "~/sharedComponents/buttons/Switcher";
import { Flex } from "~/sharedComponents/layout";
import { BlockForFullStory, blockForFullStory } from "~/utils/blockForFullStory";
import { Highlighter } from "../highlighter";
import { inputTheme } from "../inputs";
import { getItemId, getItemLabel, ItemKey } from "../list";
import { ItemAdditionalOptions, VirtListRowProps } from "../list/BaseList";
import { ListProps } from "../list/List";
import { ListWithGroupsRowWrapper } from "../list/ListGroups/ListGroup";
import { getGroupedListItemHeight } from "../list/ListGroups/listGroupsHelpers";
import { ListGroupTitle } from "../list/ListGroups/ListGroupTitle";
import { SearchableList } from "../list/SearchableList";
import { SearchField } from "../list/SearchField";
import * as listStyles from "../list/VirtList.less";
import { useFocusMultiselectInput } from "../select/multiSelect/MultiSelectInput";
import { Checkbox, checkboxTheme } from "./Checkbox";
import * as styles from "./MultiCheckbox.less";

export type OptionType<TValue = ItemKey> = {
    label: string;
    id?: TValue;
    checked?: boolean;
    order?: number;
    groupIds?: number[];
    group?: boolean;
    groupKey?: string;
    groupName?: string;
    customItemValue?: string;
    hierarchyPart?: string;
};

export type MultiCheckboxProps = BlockForFullStory &
    Pick<
        ListProps<OptionType>,
        | "items"
        | "isVirtualized"
        | "listWidth"
        | "pageSize"
        | "filterItem"
        | "noItemsClassName"
        | "isLoading"
        | "additionalRows"
        | "auditEvent"
    > & {
        onChangeAction: (value: OptionType, newValue: boolean) => void;
        groupBy?: F1<OptionType[], OptionType[]>;
        renderSwitcher?: boolean;
        getTexForItem?: (item: OptionType, searchText: string) => string;
        /**
         callback for creating hint element for item
         @returns {React.ReactNode } that will be rendered as hint
         */
        hint?: (item: OptionType) => React.ComponentProps<typeof SwitcherWithHint>["hint"];
        getHelpIcon?: (item: OptionType) => React.ComponentProps<typeof SwitcherWithHint>["helpIcon"];
        withHierarchy?: boolean;
    };

export const MultiCheckbox: FC<MultiCheckboxProps> = props => {
    const { items, groupBy, onChangeAction, ...listProps } = props;
    const listRow = useListRow(props);
    const { focusInput, inputRef } = useFocusMultiselectInput<SearchField>();
    const onChange = (item: OptionType & ItemAdditionalOptions) => {
        if (item.addRow) {
            inputRef.current?.handleFilterTextChange("");
        }

        onChangeAction(item, !item.checked);
    };

    const options = useMemo(() => (groupBy ? groupBy(items) : items), [groupBy, items]);
    const itemHeight = listProps.withHierarchy ? 32 : 28;

    return (
        <div onClick={focusInput}>
            <SearchableList
                items={options}
                inputRef={inputRef}
                itemHeight={itemHeight}
                {...listProps}
                searchFieldTheme={inputTheme.naked_list_search}
                onSelectItem={onChange}
                getItemLabel={getItemLabel}
                getItemHeight={getGroupedListItemHeight(itemHeight)}
                listRowComponent={listRow}
                getItemKey={getItemId}
            />
        </div>
    );
};

const useListRow = (multiCheckboxProps: MultiCheckboxProps) => {
    const propsRef = useRef(multiCheckboxProps);
    propsRef.current = multiCheckboxProps;

    return useCallback<FC<VirtListRowProps<OptionType>>>(args => {
        const { item, textForHighlight } = args;
        const className = classNames(args.isFocused && listStyles.focused);

        return (
            <ListWithGroupsRowWrapper {...args}>
                {item.group ? (
                    <ListGroupTitle title={item.label} />
                ) : !propsRef.current.renderSwitcher ? (
                    getCheckBoxComponent(item, textForHighlight, className, propsRef.current)
                ) : (
                    getSwitcherComponent(item, textForHighlight, className, propsRef.current)
                )}
            </ListWithGroupsRowWrapper>
        );
    }, []);
};

const getCheckBoxComponent = (
    item: OptionType,
    textForHighlight: string,
    rowClassName: string,
    props: MultiCheckboxProps
) => {
    const text = props.getTexForItem ? props.getTexForItem(item, textForHighlight) : item.label;
    const hierarchyPart = !textForHighlight ? item.hierarchyPart : undefined;

    return (
        <Flex>
            <Checkbox
                withHierarchy={props.withHierarchy}
                checked={item.checked}
                theme={[checkboxTheme.list, checkboxTheme.ellipsis]}
                blockForFullStory={props.blockForFullStory}
                className={rowClassName}
                testId={item.label}
                // not to move focus from input to checkbox by "tab" key
                tabIndex={-1}
                hierarchyPart={hierarchyPart}
            >
                <Highlighter text={text} textForHighlight={textForHighlight} showTitle={true} />
            </Checkbox>
        </Flex>
    );
};

const getSwitcherComponent = (
    item: OptionType,
    textForHighlight: string,
    rowClassName: string,
    props: MultiCheckboxProps
) => {
    const labelledbyId = `a-l-${item.id}`;

    const label = (
        <Highlighter
            className={blockForFullStory(styles.text, props)}
            text={item.label}
            textForHighlight={textForHighlight}
            showTitle={true}
            id={labelledbyId}
        />
    );

    const Component = props.hint ? SwitcherWithHint : Switcher;

    return (
        <Component
            aria-labelledby={labelledbyId}
            isOn={item.checked}
            className={classNames(styles.switcher_wrapper, rowClassName)}
            testId={item.label}
            label={label}
            hint={props.hint?.(item)}
            helpIcon={props?.getHelpIcon?.(item)}
        />
    );
};
